import { useCallback, useEffect, useState } from 'react';

import {
  IDLE_STATE,
  createBrowserProbeDeps,
  probeUntilHealthy,
  type ProbeState,
} from './healthProbe';

export interface HealthProbeResult {
  readonly state: ProbeState;
  /** Relanza la sonda desde cero, cancelando la anterior. */
  readonly retry: () => void;
}

/**
 * Capa React sobre {@link probeUntilHealthy}.
 *
 * Deliberadamente delgada: toda la logica de reintentos, backoff y deteccion
 * de arranque en frio vive en el modulo puro, que se prueba sin DOM.
 */
export function useHealthProbe(httpBaseUrl: string): HealthProbeResult {
  const [state, setState] = useState<ProbeState>(IDLE_STATE);
  const [attemptKey, setAttemptKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    probeUntilHealthy(
      {
        httpBaseUrl,
        signal: controller.signal,
        onState: (next) => {
          if (!controller.signal.aborted) {
            setState(next);
          }
        },
      },
      createBrowserProbeDeps(),
    ).then(
      (final) => {
        if (!controller.signal.aborted) {
          setState(final);
        }
      },
      () => {
        // `probeUntilHealthy` no lanza; este brazo solo cubre un aborto.
      },
    );

    return () => {
      controller.abort();
    };
  }, [httpBaseUrl, attemptKey]);

  const retry = useCallback(() => {
    setState(IDLE_STATE);
    setAttemptKey((previous) => previous + 1);
  }, []);

  return { state, retry };
}
